/**
 * Gilt eine Antwort als gegeben?
 *
 * Die kanonische Füllprüfung des Pakets — sie entscheidet gleichermaßen über
 * den Fortschritt (`computeFormProgress`) und über die Bedingungsformen
 * `isFilled` und `notEquals`:
 *
 * - `null`/`undefined` (Frage nie berührt) → leer
 * - String → leer, wenn nach dem Trimmen nichts übrig bleibt
 * - Array (Mehrfachauswahl) → leer, wenn kein Haken gesetzt ist
 * - alles andere — auch `false` und `0` — → beantwortet
 */
export const isAnswerFilled = (value) => {
    if (value == null)
        return false;
    if (typeof value === "string")
        return value.trim() !== "";
    if (Array.isArray(value))
        return value.length > 0;
    return true;
};
/**
 * Wertet EINE Bedingung gegen die Antworten aus.
 *
 * Die Reihenfolge der Prüfungen ist zugleich ihr Vorrang: `isFilled` schlägt
 * `equals`, das schlägt `equalsAny`, das schlägt `notEquals` — die erste
 * gesetzte Form entscheidet allein. Eine fehlende Bedingung und eine
 * Bedingung ohne bekannte Form gelten als erfüllt: Ein unbekannter Operator
 * darf eine Frage niemals verstecken.
 *
 * `equals`/`equalsAny` vergleichen den GANZEN Antwortwert. Eine
 * Mehrfachauswahl (`string[]`) trifft deshalb bewusst nie zu — dafür gibt es
 * kein „enthält"-Vokabular.
 *
 * `notEquals` verlangt zusätzlich eine gefüllte Antwort: „etwas anderes als
 * X" heißt nicht „gar nichts". Maßstab ist `isAnswerFilled`, eine leere
 * Mehrfachauswahl (`[]`) und ein reiner Leerzeichen-String zählen also als
 * unbeantwortet.
 */
export const matchesCondition = (condition, answers) => {
    if (!condition)
        return true;
    const value = answers[condition.key];
    if (condition.isFilled)
        return isAnswerFilled(value);
    if (condition.equals != null)
        return value === condition.equals;
    if (condition.equalsAny != null)
        return condition.equalsAny.some((item) => value === item);
    if (condition.notEquals != null)
        return isAnswerFilled(value) && value !== condition.notEquals;
    return true;
};
/**
 * Ist die Frage selbst sichtbar?
 *
 * `showWhen` und — falls gesetzt — jede einzelne Bedingung aus `showWhenAll`
 * müssen erfüllt sein (UND-Verknüpfung). Eine Frage ohne Bedingungen ist
 * immer sichtbar.
 */
export const isQuestionVisible = (question, answers) => matchesCondition(question.showWhen, answers) &&
    (question.showWhenAll ?? []).every((condition) => matchesCondition(condition, answers));
/**
 * Ist der Folgefragen-Block („wenn ja, …") der Frage sichtbar?
 *
 * Er hängt allein an `showFollowUpWhen`; die Sichtbarkeit der Frage selbst
 * prüfen die Aufrufer vorher über `isQuestionVisible`.
 */
export const isFollowUpVisible = (question, answers) => matchesCondition(question.showFollowUpWhen, answers);
